import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { AuthStorageService, BaseSiteService, TranslationService, UserIdService } from '@spartacus/core';
import { Observable, combineLatest } from 'rxjs';
import { debounceTime, map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ShimanoCountryService } from '../country/shimano-country.service';
import { CustomTranslationService } from '../language/custom-translation.service';
import { ShimanoLanguageService } from '../language/shimano-language.service';

export interface GetManagementEmployeesPayload {
  currentPage?: number;
  pageSize?: number;
  sort?: string;
}

export interface PostAddEmployeesPayload {
  fields?: string;
  active?: boolean;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  id?: string;
  email?: string;
  customerId?: string;
  shipToAddrsId?: any;
  storeFrontPermissions?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ManagementEmployeeService {
  baseSite$: Observable<string> = this.baseService.getActive();
  userCurrent$: Observable<string> = this.auth.getUserId();
  occUrl = this.meta.getTag('name=occ-backend-base-url');
  url = `${this.occUrl.content}${environment.prefix}${this.baseSiteId}`;
  headers = new HttpHeaders().set('Content-Type', 'application/json');

  sortLabels: Observable<{
    byStatus: string;
    byLastNameAsc: string;
    byLastNameDesc: string;
  }>;

  constructor(
    protected http: HttpClient,
    protected auth: UserIdService,
    protected authStorageService: AuthStorageService,
    protected baseService: BaseSiteService,
    protected translation: TranslationService,
    private meta: Meta,
    private languageService: ShimanoLanguageService,
    private countryService: ShimanoCountryService,
    private customTranslationService: CustomTranslationService
  ) {}

  get baseSiteId(): string {
    let baseSiteId = '';
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  get userCurrent(): string {
    let userCurrent = '';
    this.userCurrent$.pipe(take(1)).subscribe((userId) => (userCurrent = userId));
    return userCurrent;
  }

  get tokenId(): string {
    let tokenId = '';
    this.authStorageService
      .getToken()
      .pipe(take(1))
      .subscribe((tokenIdData) => (tokenId = tokenIdData.access_token));
    return tokenId;
  }

  get activeLanguage(): string {
    let activeLanguage = '';
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  get activeCountry(): string {
    let activeCountry = '';
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  patchEditEmployee(payload: PostAddEmployeesPayload): Observable<any> {
    const body = {
      active: payload.active,
      firstName: payload.firstName,
      lastName: payload.lastName,
      jobTitle: payload.jobTitle,
      defaultAddress: {
        id: payload.id,
      },
      email: payload.email,
      customerId: payload.customerId,
      shipToAddresses: payload.shipToAddrsId,
      storeFrontPermissions: payload.storeFrontPermissions,
    };

    return this.http
      .patch(`${this.url}/users/${this.userCurrent}/management/employee`, JSON.stringify(body), {
        headers: this.headers,
      })
      .pipe(map((data) => data));
  }

  postAddEmployee(payload: PostAddEmployeesPayload): Observable<any> {
    const body = {
      active: payload.active,
      firstName: payload.firstName,
      lastName: payload.lastName,
      jobTitle: payload.jobTitle,
      defaultAddress: {
        id: payload.id,
      },
      email: payload.email,
      customerId: payload.customerId,
      shipToAddresses: payload.shipToAddrsId,
      storeFrontPermissions: payload.storeFrontPermissions,
    };

    return this.http
      .post(`${this.url}/users/${this.userCurrent}/management/employee`, JSON.stringify(body), {
        headers: this.headers,
      })
      .pipe(map((data) => data));
  }

  getManagementEmployees(payload: GetManagementEmployeesPayload): Observable<any> {
    const params = new HttpParams()
      .set('currentPage', payload.currentPage as any)
      .set('pageSize', payload.pageSize as any)
      .set('sort', payload.sort as any);

    return this.http
      .get(`${this.url}/users/${this.userCurrent}/management/employees`, {
        params,
        headers: this.headers,
      })
      .pipe(map((data) => data));
  }

  getSortLabels(isCustomTranslationsEnabled = false): Observable<{
    byStatus: string;
    byLastNameAsc: string;
    byLastNameDesc: string;
  }> {
    return combineLatest([
      this.translation.translate('myAccount.sortLabels.status'),
      this.translation.translate('myAccount.sortLabels.lastNameAsc'),
      this.setCustomTranslation$(isCustomTranslationsEnabled, 'myAccount.sortLabels.lastNameDesc'),
    ]).pipe(
      debounceTime(0),
      map(([textStatus, textLastNameAsc, textLastNameDesc]) => {
        return {
          byStatus: textStatus,
          byLastNameAsc: textLastNameAsc,
          byLastNameDesc: textLastNameDesc,
        };
      })
    );
  }

  setCustomTranslation$(isCustomTranslationsEnabled: boolean, translationKey: string): Observable<string> {
    const optionsContext = isCustomTranslationsEnabled ? [this.activeCountry, this.activeLanguage].join('-') : null;
    return this.translation.translate(translationKey, { context: optionsContext });
  }
}
